import {
  ArrayInput,
  AutocompleteArrayInput,
  AutocompleteInput,
  FormDataConsumer,
  maxLength,
  NumberInput,
  ReferenceArrayInput,
  ReferenceInput,
  required,
  SelectInput,
  SimpleFormIterator,
  TextInput,
  useChoicesContext,
} from "react-admin";
import { useEffect, useState } from "react";
import { useWatch } from "react-hook-form";
import { Grid } from "@mui/material";
import CustomDateInput from "server/components/Fields/CustomDateInput";
import { CurriculumItemTypeChoices } from "utils/choices";
import { CurriculumItemTypes, SpecializationTypes } from "utils/constants";
import { specialistRequests } from "api/specialistApi";

const SpecializationTypeWatcher = ({ onChange }) => {
  const { allChoices } = useChoicesContext();
  const specializationId = useWatch({ name: "specializationId" });

  useEffect(() => {
    const selected = allChoices?.find(c => c.id === specializationId);
    onChange(selected?.type ?? null);
  }, [specializationId, allChoices, onChange]);

  return null;
};

const SaveForm = () => {
  const [specializationType, setSpecializationType] = useState(null);
  const selectedCourseDefinitionIds = useWatch({ name: "courseDefinitionIds" }) || [];

  return (
    <Grid
      container
      spacing={2}
    >
      <Grid
        item
        xs={12}
        md={6}
      >
        <ReferenceInput
          source="specializationId"
          reference={specialistRequests.getSpecializationsSelect()}
        >
          <>
            <AutocompleteInput
              label="Специалност"
              optionText={rec => `${rec?.nameBg} | ${rec?.typeText}`}
              filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
              validate={[required()]}
              fullWidth
            />
            <SpecializationTypeWatcher onChange={setSpecializationType} />
          </>
        </ReferenceInput>
      </Grid>

      <Grid
        item
        xs={12}
        md={6}
      >
        <CustomDateInput
          source="validFrom"
          label="Валиден от"
          required
          validate={required()}
          noTime
          fullWidth
        />
      </Grid>

      {specializationType === SpecializationTypes.NonClinical ? (
        <>
          <Grid
            item
            xs={12}
          >
            <ArrayInput
              source="modules"
              label="Модули"
              defaultValue={[{}]}
            >
              <SimpleFormIterator>
                <Grid
                  container
                  spacing={2}
                >
                  <Grid
                    item
                    xs={12}
                    md={6}
                  >
                    <TextInput
                      source="nameBg"
                      label="Наименование (БГ)"
                      validate={[required(), maxLength(200)]}
                      fullWidth
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}
                  >
                    <TextInput
                      source="nameEn"
                      label="Наименование (EN)"
                      validate={[required(), maxLength(200)]}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </SimpleFormIterator>
            </ArrayInput>
          </Grid>
          <Grid
            item
            xs={12}
          >
            <ArrayInput
              source="items"
              label="Теми"
              defaultValue={[{}]}
            >
              <SimpleFormIterator>
                <Grid
                  container
                  spacing={2}
                >
                  <Grid
                    item
                    xs={12}
                    md={6}
                  >
                    <TextInput
                      source="nameBg"
                      label="Наименование (БГ)"
                      validate={[required(), maxLength(500)]}
                      fullWidth
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}
                  >
                    <TextInput
                      source="nameEn"
                      label="Наименование (EN)"
                      validate={[required(), maxLength(500)]}
                      fullWidth
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={4}
                  >
                    <SelectInput
                      choices={CurriculumItemTypeChoices}
                      source="type"
                      label="Тип"
                      validate={[required()]}
                    />
                  </Grid>

                  <Grid
                    item
                    xs={12}
                    md={4}
                  >
                    <FormDataConsumer>
                      {({ formData }) => (
                        <SelectInput
                          source="moduleName"
                          label="Модул"
                          choices={(formData.modules || [])
                            .filter(m => m?.nameBg)
                            .map(m => ({ id: m.nameBg, name: m.nameBg }))}
                          fullWidth
                          validate={required()}
                          emptyText={false}
                        />
                      )}
                    </FormDataConsumer>
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={4}
                  >
                    <FormDataConsumer>
                      {({ scopedFormData }) =>
                        scopedFormData?.type !== CurriculumItemTypes.Colloquium && (
                          <NumberInput
                            source="durationHours"
                            label="Часове продължителност"
                            validate={required()}
                            fullWidth
                            onWheel={e => e.target.blur()}
                          />
                        )
                      }
                    </FormDataConsumer>
                  </Grid>
                </Grid>
              </SimpleFormIterator>
            </ArrayInput>
          </Grid>
        </>
      ) : null}
      {specializationType === SpecializationTypes.Clinical ? (
        <Grid
          item
          xs={12}
        >
          <ReferenceArrayInput
            source="courseDefinitionIds"
            reference={specialistRequests.getPostgraduateCourseDefinitionsSelect()}
            filter={{ filterByUser: true, ExcludeIds: selectedCourseDefinitionIds }}
          >
            <AutocompleteArrayInput
              label="Определение на курс"
              optionText={rec => `${rec.name} | ${rec.typeNameBg} | ${rec.clinicNameBg}`}
              fullWidth
            />
          </ReferenceArrayInput>
        </Grid>
      ) : null}
    </Grid>
  );
};

export default SaveForm;
